import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { parse } from 'date-fns'
import styled, { css } from 'styled-components'
import {
  GqlViewerHasAdditionalInfoFilledDocument,
  useUpdateUserInitialInfoMutation,
} from 'generated/graphql'
import Modal from '@app.components/Modal'
import { useChattingContext } from '@app.feature/chatting/chattingContext/module/ChattingContext'
import SignupSubmitButton from '@app.feature/signup/signup/components/SignupSubmitButton'
import SignupInterest from '@app.feature/signup/signup/components/signupInterest/SignupInterest'
import ReceiveGuideDesktop from '@app.feature/userInfoModal/desktop/components/ReceiveGuide.desktop'
import { useGetPreviousUserInfo } from '@app.feature/userInfoModal/hooks/useGetPreviousUserInfo'
import { useCurrentUserContext } from 'hooks/useCurrentUser'
import CloseIcon from 'svgIcons/Close'
import UserInfoModalInfo from '../components/UserInfoModalInfo'
import userInfoModalSchema from '../schema/userInfoModalSchema'
import { UserInfoModalType } from '../type/userInfoModalType'
import UserInfoExitModalDesktop from './components/UserInfoExitModal.desktop'

interface IFProps {
  isModalOpen: boolean
  handleModalClose: () => void
}

const UserInfoModal = ({ isModalOpen, handleModalClose }: IFProps) => {
  const { user } = useCurrentUserContext()
  const { reportToChatting } = useChattingContext()

  const { getViewerData } = useGetPreviousUserInfo()

  const formMethods = useForm<UserInfoModalType>({
    mode: 'onChange',

    resolver: yupResolver(userInfoModalSchema),
    defaultValues: async () => {
      const viewerData = await getViewerData()

      return {
        ...viewerData,
        birthday: '',
        phoneNumber: user?.phoneNumber || '',
        subscribeSMS: false,
        smsAuthedToken: '',
      }
    },
  })

  const { handleSubmit } = formMethods

  const [updateUserInitialInfo] = useUpdateUserInitialInfoMutation({
    onCompleted: () => {
      reportToChatting({
        type: 'reportChatRefreshMyChatRoom',
      })
      handleModalClose()
    },
    refetchQueries: [GqlViewerHasAdditionalInfoFilledDocument],
  })

  const handleSubmitTest = (data: UserInfoModalType) => {
    updateUserInitialInfo({
      variables: {
        input: {
          education: {
            finalEducationType: data.schoolType,
            schoolName: data.school.name,
            major: data.schoolMajor?.name,
          },
          birthday: parse(data.birthday, 'yyyyMMdd', new Date()).setHours(12),
          phoneNumber: data.phoneNumber,
          smsAuthedToken: data.smsAuthedToken,
          preferJobCategoryIds: data.preferJobCategoryIds,
          preferActivityTypes: data.preferActivityTypes.map((num) =>
            Number(num),
          ),
          preferOrganizationIds: data.preferOrganizationIds,
          subscribeSMS: data.subscribeSMS,
        },
      },
    })
  }

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const handleToggleConfirmModal = () => {
    setIsConfirmModalOpen(!isConfirmModalOpen)
  }

  const handleCloseAllModal = () => {
    setIsConfirmModalOpen(false)
    handleModalClose()
  }

  return (
    /* TODO Type error: Type instantiation is excessively deep and possibly infinite. */
    /* @ts-ignore  */
    <FormProvider {...formMethods}>
      <Modal open={isModalOpen} disableBackDropClose>
        <form onSubmit={handleSubmit(handleSubmitTest)}>
          <StyledWrapper>
            <main className="join-container">
              <i
                className="close-icon"
                role="button"
                onClick={handleToggleConfirmModal}
              >
                <CloseIcon width={16} height={16} fill="#333" />
              </i>
              <header className="join-title">
                <h2>
                  정보를 입력하면 저희가
                  <br />
                  <span>딱 맞는 커리어 정보</span>를 알려드릴게요!
                </h2>
              </header>
              <section>
                <SignupInterest />
              </section>
              <section className="field-school-section">
                <UserInfoModalInfo />
              </section>
              <section className="guide-section">
                <ReceiveGuideDesktop />
              </section>
              <section className="modal-submit-button">
                <SignupSubmitButton buttonText="선택 완료" />
              </section>
            </main>
          </StyledWrapper>
        </form>
        <UserInfoExitModalDesktop
          isOpen={isConfirmModalOpen}
          onClickCancel={handleToggleConfirmModal}
          onClickConfirm={handleCloseAllModal}
        />
      </Modal>
    </FormProvider>
  )
}

export default UserInfoModal

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-height: 92vh;
    overflow-y: auto;
    ::-webkit-scrollbar {
      display: block;
    }
    ::-webkit-scrollbar-track,
    ::-webkit-scrollbar-button {
      display: none;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 15px;
      background-color: ${theme.background.bg_1};
    }
    width: 502px;
    padding: 25px 20px 40px;
    border-radius: 15px;
    background-color: white;

    .join-container {
      & > .divider {
        border: none;
        height: 1px;
        background-color: ${theme.background.bg_1};
        margin: 32px 0;
      }
      > .close-icon {
        display: flex;
        cursor: pointer;
        flex-direction: row-reverse;
      }
      .join-title {
        h2 {
          text-align: center;
          font-size: 20px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.4;
          letter-spacing: -0.8px;
          color: #333;
          > span {
            color: ${theme.color.primary_2};
          }
        }
      }
      .field-school-section {
        margin-top: 24px;
      }
      .guide-section {
        margin-top: 32px;
      }
      .modal-submit-button {
        width: 120px;
        margin: 0 auto;
        padding-top: 8px;
      }
    }
    p {
      margin: 0 !important;
    }
    .pointer {
      cursor: pointer;
    }
  `}
`
